/**
 * 语言解析器（与 cartsee-front 对齐）
 *
 * 优先级：URL ?lang= > localStorage['arco-lang'] > navigator.language > 'zh-CN'
 * URL 参数优先是为了跨系统跳转时发送方能强制覆盖接收方的语言偏好。
 * 读到 URL 参数会立即写入 localStorage 并从 URL 清除，避免参数在后续导航中污染。
 */

export type Locale = 'zh-CN' | 'en-US';

const STORAGE_KEY = 'arco-lang';
const VALID_LOCALES: readonly Locale[] = ['zh-CN', 'en-US'];
const DEFAULT_LOCALE: Locale = 'zh-CN';

function isValidLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (VALID_LOCALES as readonly string[]).includes(value);
}

function normalizeNavigatorLang(lang: string): Locale {
  const lower = lang.toLowerCase();
  if (lower === 'zh-cn' || lower === 'zh') return 'zh-CN';
  if (lower === 'en-us' || lower === 'en') return 'en-US';
  if (lower.startsWith('en')) return 'en-US';
  if (lower.startsWith('zh')) return 'zh-CN';
  return DEFAULT_LOCALE;
}

function getFromStorage(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isValidLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

function getFromUrlParam(): Locale | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    return isValidLocale(lang) ? lang : null;
  } catch {
    return null;
  }
}

function getFromBrowser(): Locale {
  try {
    const lang = navigator.language || (navigator.languages && navigator.languages[0]) || '';
    return normalizeNavigatorLang(lang);
  } catch {
    return DEFAULT_LOCALE;
  }
}

// 从 URL 清掉 ?lang= 参数（保留其他 query），避免参数在后续导航/复制链接中扩散
function stripLangFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('lang')) {
      url.searchParams.delete('lang');
      const qs = url.searchParams.toString();
      const newUrl = url.pathname + (qs ? '?' + qs : '') + url.hash;
      history.replaceState(null, '', newUrl);
    }
  } catch { /* ignore */ }
}

export function resolveLocale(source?: string): Locale {
  // URL ?lang= 优先 —— 跨系统传入的语言强制覆盖本地偏好
  const fromUrl = getFromUrlParam();
  if (fromUrl) {
    try { localStorage.setItem(STORAGE_KEY, fromUrl); } catch { /* ignore */ }
    stripLangFromUrl();
    if (source && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log(`[i18n] ${source}: resolved from URL ?lang= -> ${fromUrl}`);
    }
    return fromUrl;
  }

  const fromStorage = getFromStorage();
  if (fromStorage) {
    if (source && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log(`[i18n] ${source}: resolved from localStorage -> ${fromStorage}`);
    }
    return fromStorage;
  }

  const fromBrowser = getFromBrowser();
  if (source && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log(`[i18n] ${source}: resolved from navigator.language -> ${fromBrowser}`);
  }
  return fromBrowser;
}

export { isValidLocale, STORAGE_KEY };
